import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowsClockwise, Copy, DownloadSimple } from '@phosphor-icons/react';
import { CompanyService } from '../../service/CompanyService';
import CompletionLogsService, {
  type CompletionLogRow,
  type CompletionLogsQueryParams,
  type LogSource,
} from '../../service/CompletionLogsService';
import { useAuthStore } from '../../store/storeAuth';

const PAGE_SIZE = 25;

interface CompanyUserOption {
  id: string;
  label: string;
}

const SOURCE_COLORS: Record<LogSource, string> = {
  hot: '#F5831F',
  cold: '#4BA3DC',
  merged: '#9A8AC0',
};

const PROVIDERS = ['anthropic', 'openai', 'gemini', 'groq', 'perplexity', 'xai', 'mistral'];

function toDateInput(d: Date): string {
  return d.toISOString().split('T')[0];
}

function defaultFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toDateInput(d);
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatCost(cost: number | string): string {
  const n = Number(cost);
  return n === 0 ? '—' : n.toFixed(6);
}

function truncate(s: string, n = 8): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function statusColor(status: number): 'success' | 'error' | 'default' {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400) return 'error';
  return 'default';
}

const CompletionLogsPage: React.FC = () => {
  const authUser = useAuthStore(state => state.user);
  const isOrgAdmin = authUser?.companyRole === 'admin';
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => toDateInput(new Date()));
  const [statusFilter, setStatusFilter] = useState<'ok' | 'error' | ''>('');
  const [modelFilter, setModelFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [companyUsers, setCompanyUsers] = useState<CompanyUserOption[]>([]);
  const [rows, setRows] = useState<CompletionLogRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [source, setSource] = useState<LogSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(
    (cursor?: string): CompletionLogsQueryParams => {
      const params: CompletionLogsQueryParams = { from, to, limit: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (modelFilter.trim()) params.model = modelFilter.trim();
      if (providerFilter) params.provider = providerFilter;
      if (userFilter) params.userId = userFilter;
      if (cursor) params.cursor = cursor;
      return params;
    },
    [from, to, statusFilter, modelFilter, providerFilter, userFilter],
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setRows([]);
    setNextCursor(null);
    setSource(null);
    try {
      const result = await CompletionLogsService.query(buildParams());
      setRows(result.data);
      setNextCursor(result.nextCursor);
      setSource(result.source);
    } catch (err) {
      console.error('Failed to fetch completion logs', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await CompletionLogsService.query(buildParams(nextCursor));
      setRows(prev => [...prev, ...result.data]);
      setNextCursor(result.nextCursor);
      setSource(result.source);
    } catch (err) {
      console.error('Failed to load more completion logs', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await CompletionLogsService.exportCsv({
        from,
        to,
        status: statusFilter || undefined,
        model: modelFilter.trim() || undefined,
        provider: providerFilter || undefined,
        userId: userFilter || undefined,
      });
    } catch (err) {
      console.error('Failed to export completion logs', err);
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(err => console.error('Copy failed', err));
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (!isOrgAdmin || !authUser?.selectedCompanyId) return;
    CompanyService.getAllCompanyUsers(authUser.selectedCompanyId)
      .then(res => {
        const options = (res.data ?? []).map(item => ({
          id: item.user.id,
          label: item.user.name || item.user.email,
        }));
        setCompanyUsers(options);
      })
      .catch(err => console.error('Failed to load company users', err));
  }, [isOrgAdmin, authUser?.selectedCompanyId]);

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Completion Logs
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-end' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={e => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={e => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <Select
            size="small"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as 'ok' | 'error' | '')}
            displayEmpty
            sx={{ width: 140 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="ok">OK (2xx)</MenuItem>
            <MenuItem value="error">Error (4xx/5xx)</MenuItem>
          </Select>
          <Select
            size="small"
            value={providerFilter}
            onChange={e => setProviderFilter(e.target.value)}
            displayEmpty
            sx={{ width: 150 }}
          >
            <MenuItem value="">All providers</MenuItem>
            {PROVIDERS.map(p => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
          {isOrgAdmin && (
            <Select
              size="small"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              displayEmpty
              sx={{ width: 200 }}
            >
              <MenuItem value="">All users</MenuItem>
              {companyUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.label}
                </MenuItem>
              ))}
            </Select>
          )}
          <TextField
            label="Model"
            size="small"
            value={modelFilter}
            onChange={e => setModelFilter(e.target.value)}
            placeholder="e.g. claude-opus"
            sx={{ width: 200 }}
          />
          <IconButton onClick={fetchLogs} title="Refresh" color="primary">
            <ArrowsClockwise />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={exporting ? <CircularProgress size={14} /> : <DownloadSimple />}
            onClick={handleExport}
            disabled={exporting}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Flux In</TableCell>
                <TableCell align="right">Flux Out</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Latency</TableCell>
                <TableCell align="center">Mode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No logs found for the selected range.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      {formatTimestamp(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to copy user ID">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {row.userName ? truncate(row.userName, 20) : truncate(row.userId, 8)}
                          </Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(row.userId)}>
                            <Copy size={12} />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{row.model}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{row.provider}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.errorCode ?? String(row.status)}
                        color={statusColor(row.status)}
                        size="small"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {Number(row.fluxInputTokens).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {Number(row.fluxOutputTokens).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {formatCost(row.cost)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.latencyMs.toLocaleString()} ms
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.stream ? 'Stream' : 'Sync'}
                        size="small"
                        variant="outlined"
                        color={row.stream ? 'info' : 'default'}
                        sx={{ fontSize: 10 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {nextCursor && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}

        {!loading && rows.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block', textAlign: 'right' }}
          >
            {rows.length} row{rows.length !== 1 ? 's' : ''} loaded
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CompletionLogsPage;
